import { useGameStore } from '@/store/gameState'
import { useActivity } from '@/hooks/useTimer'
import { PLACES, REGIONS } from '@/data/places'
import { HUNT_LIST, type HuntType } from '@/data/hunts'
import { TRAVEL_COST, TRAVEL_TIME_MINUTES } from '@/data/constants'
import styles from './ExploreView.module.css'

export function ExploreView() {
  const focusSession = useGameStore((s) => s.focusSession)
  const activity = useActivity()

  if (focusSession) {
    return <FocusBlocker />
  }

  if (activity.isActive) {
    return <ActivityProgress />
  }

  return <ExploreContent />
}

function FocusBlocker() {
  return (
    <div className={styles.blocker}>
      <div className={styles.blockerIcon}>⛺</div>
      <h3 className={styles.blockerTitle}>You're focusing...</h3>
      <p className={styles.blockerText}>
        Your explorer has set up camp. Exploration awaits after your session.
      </p>
    </div>
  )
}

function ActivityProgress() {
  const activity = useActivity()
  const gameActivity = useGameStore((s) => s.activity)

  const formatTime = (minutes: number) => {
    const m = Math.floor(Math.max(0, minutes))
    const s = Math.floor((Math.max(0, minutes) - m) * 60)
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  const isTravel = activity.type === 'travel'
  const destPlace = isTravel && gameActivity?.dest ? PLACES[gameActivity.dest] : null

  return (
    <div className={styles.activityContainer}>
      <div className={`${styles.activityCard} breathe`}>
        <div className={styles.activityIcon}>
          {isTravel ? '🚶' : '🔍'}
        </div>
        <div className={styles.activityInfo}>
          <h3 className={styles.activityTitle}>
            {isTravel
              ? `Traveling to ${destPlace?.name ?? 'Unknown'}...`
              : 'Hunting for Echoes...'}
          </h3>
          <p className={styles.activityTime}>
            {formatTime(activity.remainingMinutes)} remaining
          </p>
        </div>
        <div className={styles.progressBar}>
          <div
            className={styles.progressFill}
            style={{ width: `${activity.progress * 100}%` }}
          />
        </div>
      </div>

      <p className={styles.activityHint}>
        Starting a focus session will cancel this activity.
      </p>
    </div>
  )
}

function ExploreContent() {
  const place = useGameStore((s) => s.place)
  const discovered = useGameStore((s) => s.discovered)
  const getEnergy = useGameStore((s) => s.getEnergy)
  const startHunt = useGameStore((s) => s.startHunt)
  const startTravel = useGameStore((s) => s.startTravel)
  const isPlaceUnlocked = useGameStore((s) => s.isPlaceUnlocked)

  const currentPlace = PLACES[place]
  const currentRegion = currentPlace ? REGIONS[currentPlace.region] : null
  const { total: energy } = getEnergy()

  const adjacentPlaces = (currentPlace?.adjacentPlaces ?? [])
    .map((id) => PLACES[id])
    .filter((p): p is NonNullable<typeof p> => p !== undefined)

  const handleHunt = (huntType: HuntType) => {
    startHunt(huntType)
  }

  const handleTravel = (destId: string) => {
    startTravel(destId)
  }

  return (
    <div className={`${styles.container} stagger`}>
      {/* Location Card */}
      <div className={styles.locationCard}>
        <div className={styles.locationGlow} />
        <div className={styles.locationContent}>
          <h2 className={styles.placeName}>{currentPlace?.name}</h2>
          <p className={styles.regionName}>{currentRegion?.name}</p>
        </div>
      </div>

      {/* Hunt Section */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Hunt for Echoes</h3>
        <div className={styles.huntGrid}>
          {HUNT_LIST.map((hunt) => {
            const canAfford = energy >= hunt.energy

            return (
              <button
                key={hunt.type}
                className={`${styles.huntBtn} ${!canAfford ? styles.huntBtnDisabled : ''}`}
                onClick={() => handleHunt(hunt.type)}
                disabled={!canAfford}
              >
                <span className={styles.huntLabel}>{hunt.label}</span>
                <div className={styles.huntStats}>
                  <span className={styles.huntStat}>
                    <span className={styles.energyIcon}>⚡</span>
                    {hunt.energy}
                  </span>
                  <span className={styles.huntStat}>{hunt.timeMinutes}min</span>
                  <span className={styles.huntStat}>
                    {Math.round(hunt.successRate * 100)}%
                  </span>
                </div>
              </button>
            )
          })}
        </div>
      </section>

      {/* Travel Section */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>
          Travel
          <span className={styles.travelCost}>
            {TRAVEL_COST}⚡ · {TRAVEL_TIME_MINUTES}min
          </span>
        </h3>

        {adjacentPlaces.length > 0 ? (
          <div className={styles.travelGrid}>
            {adjacentPlaces.map((dest) => {
              const unlocked = isPlaceUnlocked(dest.id)
              const canAfford = energy >= TRAVEL_COST

              return (
                <button
                  key={dest.id}
                  className={`${styles.travelBtn} ${!unlocked ? styles.travelBtnLocked : ''}`}
                  onClick={() => handleTravel(dest.id)}
                  disabled={!unlocked || !canAfford}
                >
                  <span className={styles.travelName}>{dest.name}</span>
                  {!unlocked && (
                    <span className={styles.travelLock}>
                      🔒 {dest.unlockEchoes} Echoes
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        ) : (
          <p className={styles.noTravel}>No adjacent places to travel to.</p>
        )}

        {/* Unlock hint */}
        {discovered.length < 3 && (
          <p className={styles.unlockHint}>
            Discover {3 - discovered.length} more Echo{3 - discovered.length !== 1 ? 'es' : ''} to unlock new places.
          </p>
        )}
      </section>
    </div>
  )
}
